import React, { useState, useMemo } from 'react';
import Bounce from 'react-reveal/Bounce';
import OrderCard from '../components/Order/OrderCard';
import useOrder from '../hooks/useOrder';
import { useNavigate } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import swal from 'sweetalert';

const OrderScreen = () => {
  const { orders, clearCart } = useOrder();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === orders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(orders.map((item) => item.id));
    }
  };

  const handleClearCart = () => {
    swal({
      title: 'Êtes-vous sûr ?',
      text: 'Cela supprimera tous les articles de votre panier.',
      icon: 'warning',
      buttons: ['Annuler', 'Oui, vider'],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        clearCart();
        swal('Le panier a été vidé avec succès.', {
          icon: 'success',
        });
      }
    });
  };

  const totalSelectedPrice = useMemo(() => {
    return orders
      .filter((item) => selectedIds.includes(item.id))
      .reduce((sum, item) => sum + item.quantity * item.price, 0)
      .toFixed(2);
  }, [orders, selectedIds]);

  return (
    <section className="max-w-screen-xl py-24 mx-auto px-6">
      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[70vh] text-center space-y-6">
          <img
            src="/assets/emptycart.gif"
            alt="Empty Cart"
            className="w-56 mx-auto"
          />
          <h1 className="text-3xl sm:text-4xl font-semibold text-paragaia poppins">
            Votre panier est vide 😔
          </h1>
          <p className="text-paragaia text-base sm:text-lg">
            Ajoutez vos produits préférés et vivez l’expérience <span className="font-bold">Paragaïa</span> ✨
          </p>
          <button
            onClick={() => navigate('/products')}
            className="mt-4 bg-paragaia hover:bg-[#b36b9b] text-white px-6 py-3 rounded-full transition poppins"
          >
            Parcourir les produits
          </button>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left: Items */}
          <div className="flex-1 space-y-4">
            <Bounce left>
              <div className="pb-4">
                <h1 className="text-gray-700 poppins text-3xl">
                  Mes <span className="text-paragaia font-semibold select-none">Commandes</span>
                </h1>
                <div className="bg-paragaia w-16 h-1 mt-2 rounded-full"></div>
              </div>
            </Bounce>

            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === orders.length}
                  className="accent-paragaia"
                />
                <span className="text-sm text-paragaia poppins">Sélectionner tous</span>
              </div>

              <button
                onClick={handleClearCart}
                className="flex items-center gap-2 text-paragaia border border-paragaia px-3 py-2 rounded-md hover:bg-paragaia hover:text-white transition text-sm poppins"
              >
                <FiTrash2 className="text-lg" />
                Vider le panier
              </button>
            </div>

            {orders.map((item) => (
              <OrderCard
                key={item.id}
                {...item}
                isSelected={selectedIds.includes(item.id)}
                onSelect={toggleSelect}
              />
            ))}
          </div>

          {/* Right: Summary */}
          <div className="w-full lg:w-80 h-fit bg-paragaiaLight rounded-xl p-6 shadow-md sticky top-24">
            <h2 className="text-xl font-semibold text-paragaia mb-4 poppins">
              Résumé
            </h2>

            <div className="space-y-1">
              <div className="flex justify-between items-center text-lg">
                <span className="text-gray-700">Total sélectionné :</span>
                <span className="font-bold text-paragaia">{totalSelectedPrice} TND</span>
              </div>
              <div className="text-sm text-gray-500 italic">
                +7 TND de frais de livraison
              </div>
            </div>

            <button
            onClick={() => {
              const selectedOrders = orders.filter(item => selectedIds.includes(item.id));
              const outOfStockItems = selectedOrders.filter(item => item.quantity === 0);

              if (outOfStockItems.length > 0) {
                const names = outOfStockItems.map(item => `- ${item.name}`).join('\n');
                swal({
                  title: "❌ Produit(s) en rupture de stock",
                  text: `Les articles suivants ne sont plus disponibles :\n\n${names}\n\nVeuillez patienter jusqu'à leur réapprovisionnement.`,
                  icon: "error",
                });
                return;
              }

              navigate('/checkout', {
                state: {
                  selectedOrders,
                  totalPrice: parseFloat(totalSelectedPrice),
                },
              });
            }}

              disabled={selectedIds.length === 0}
              className={`mt-6 w-full text-white py-3 rounded-lg poppins transition ${
                selectedIds.length === 0
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-paragaia hover:bg-[#b36b9b]'
              }`}
            >
              Continuer vers le paiement
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default OrderScreen;
